import type { ShallowRef } from 'vue'

import type {
  AttentionBadcase,
  AttentionBadcaseKind,
  AttentionContextRef,
  AttentionDecision,
  AttentionEvent,
  AttentionHistoryEntry,
  AttentionToolRequest,
} from './velis-attention'

import { shallowRef } from 'vue'

import { decideAttention, emptyAttentionBadcases } from './velis-attention'

interface AttentionRuntime {
  history: AttentionHistoryEntry[]
  traces: ShallowRef<AttentionDecision[]>
  badcases: ShallowRef<Record<AttentionBadcaseKind, AttentionBadcase[]>>
}

function runtime(): AttentionRuntime {
  const host = globalThis as typeof globalThis & { __velisAttention?: AttentionRuntime }
  host.__velisAttention ??= {
    history: [],
    traces: shallowRef<AttentionDecision[]>([]),
    badcases: shallowRef(emptyAttentionBadcases()),
  }
  return host.__velisAttention
}

export function attentionTraceState() {
  return runtime().traces
}

export function attentionBadcaseState() {
  return runtime().badcases
}

export function resetAttentionSession() {
  const current = runtime()
  current.history = []
  current.traces.value = []
  current.badcases.value = emptyAttentionBadcases()
}

export function attentionHistory() {
  return [...runtime().history]
}

function commitAttentionResult(decision: AttentionDecision, history: readonly AttentionHistoryEntry[], now: number) {
  const current = runtime()
  current.history = history.slice(-100)
  current.traces.value = [decision, ...current.traces.value].slice(0, 20)
  if (!decision.deduped)
    return

  const next = emptyAttentionBadcases()
  for (const kind of Object.keys(current.badcases.value) as AttentionBadcaseKind[])
    next[kind] = [...current.badcases.value[kind]]
  next['repeated-notification'] = [...next['repeated-notification'], {
    kind: 'repeated-notification' as const,
    event_id: decision.event.event_id,
    note: 'The same dedupe key was suppressed during cooldown.',
    at: now,
  }].slice(-20)
  current.badcases.value = next
}

/**
 * Records one attention decision for an event that has already arrived.
 * This function does not schedule a scan and does not call a model.
 */
export function submitAttentionEvent(
  event: AttentionEvent,
  contexts?: readonly AttentionContextRef[],
  now = Date.now(),
  executeTool?: (request: AttentionToolRequest) => void,
) {
  const result = decideAttention({
    event,
    contexts,
    now,
    history: runtime().history,
    executeTool,
  })
  commitAttentionResult(result.decision, result.history, now)
  return result.decision
}

/** Stores a decision that a companion chain already computed. */
export function recordAttentionResult(decision: AttentionDecision, history: readonly AttentionHistoryEntry[], now = Date.now()) {
  commitAttentionResult(decision, history, now)
}
